import * as Yup from 'yup';
import { PqrStatusManagement2 } from '../../../../../../shared/types/modules/pqr';
import dayjs from 'dayjs';
import {
    calculateMinDate,
    getValidDateToValidationSchema,
} from '../../../../../../shared/utils/date';

export const initialValues: PqrStatusManagement2 = {
    status: '',
    customerFilingDate: undefined,
    notificationProcedureDate: undefined,
    processingDate: undefined,
    suspensionDate: undefined,
    sendEmailExpansionInformation: false,
    reactivationDate: undefined,
    extensionDate: undefined,
    sendEmailExtension: false,
    alertSFCDate: undefined,
    responseDate: undefined,
    sendEmailResponse: false,
    notificationResponseDate: undefined,
    sacDate: undefined,
    furagDate: undefined,
    closingDate: undefined,
    observation: '',
    editFuragAndSac: false,
};

export const validationSchema = (
    isClosingStatus: boolean,
    isFollowUpStatus: boolean,
    isStatusExtensionInfo: boolean,
    isStatusReceptionInfo: boolean,
    isStatusSolution: boolean,
    isStatusProcessed: boolean,
    isStatusExtension: boolean,
    createdAt: Date | undefined
) => {
    const createdAtDate = dayjs(createdAt).isValid()
        ? dayjs(createdAt).toDate()
        : new Date();
    const schema: any = {
        observation: Yup.string()
            .required('La observación es obligatoria')
            .max(
                4294967295,
                'La observación debe tener 4294967295 caracteres alfanuméricos como máximo'
            ),

        customerFilingDate: Yup.date().max(
            createdAtDate,
            'La fecha debe ser inferior o igual a la fecha de registro'
        ),

        notificationProcedureDate: Yup.date()
            .when('customerFilingDate', (customerFilingDate, schema) => {
                const minDate =
                    getValidDateToValidationSchema(customerFilingDate);
                return minDate
                    ? schema.min(
                        minDate,
                        'La fecha debe ser superior o igual a la fecha de radicación cliente'
                    )
                    : schema;
            })
            .max(
                createdAtDate,
                'La fecha debe ser inferior o igual a la fecha de registro'
            ),

        suspensionDate: Yup.date().when(
            ['notificationProcedureDate', 'processingDate'],
            ([notificationProcedureDate, processingDate], schema) => {
                const minDate = calculateMinDate(
                    notificationProcedureDate,
                    processingDate
                );
                return schema.min(
                    minDate,
                    'La fecha debe ser superior o igual a la fecha de radicación cliente, fecha de notificación trámite, fecha de trámite y fecha de registro'
                );
            }
        ),

        reactivationDate: Yup.date().when(
            ['notificationProcedureDate', 'processingDate', 'suspensionDate'],
            (
                [notificationProcedureDate, processingDate, suspensionDate],
                schema
            ) => {
                const minDate = calculateMinDate(
                    notificationProcedureDate,
                    processingDate,
                    suspensionDate,
                    createdAtDate
                );
                return schema.min(
                    minDate,
                    'La fecha debe ser superior o igual a la fecha de radicación cliente, fecha de notificación trámite, fecha de trámite, fecha de registro y fecha de suspensión'
                );
            }
        ),
        extensionDate: Yup.date().when(
            ['notificationProcedureDate', 'processingDate', 'suspensionDate'],
            (
                [notificationProcedureDate, processingDate, suspensionDate],
                schema
            ) => {
                const minDate = calculateMinDate(
                    notificationProcedureDate,
                    processingDate,
                    suspensionDate,
                    createdAtDate
                );
                return schema.min(
                    minDate,
                    'La fecha debe ser superior o igual a la fecha de radicación cliente, fecha de notificación trámite, fecha de trámite, fecha de registro y fecha de suspensión'
                );
            }
        ),
        notificationResponseDate: Yup.date().when(
            [
                'notificationProcedureDate',
                'processingDate',
                'suspensionDate',
                'responseDate',
            ],
            (
                [
                    notificationProcedureDate,
                    processingDate,
                    suspensionDate,
                    responseDate,
                ],
                schema
            ) => {
                const minDate = calculateMinDate(
                    notificationProcedureDate,
                    processingDate,
                    suspensionDate,
                    responseDate
                );
                return schema.min(
                    minDate,
                    'La fecha debe ser superior o igual a la fecha de radicación cliente, fecha de notificación trámite, fecha de trámite, fecha de registro, fecha de suspensión y fecha de respuesta'
                );
            }
        ),

        responseDate: Yup.date().when(
            [
                'notificationProcedureDate',
                'processingDate',
                'suspensionDate',
                'extensionDate',
                'reactivationDate',
            ],
            (
                [
                    notificationProcedureDate,
                    processingDate,
                    suspensionDate,
                    extensionDate,
                    reactivationDate,
                ],
                schema
            ) => {
                const minDate = calculateMinDate(
                    notificationProcedureDate,
                    processingDate,
                    suspensionDate,
                    extensionDate,
                    reactivationDate
                );
                return schema.min(
                    minDate,
                    'La fecha debe ser superior o igual a la fecha de notificación trámite, fecha de trámite, fecha de suspensión, fecha de prórroga, fecha de reactivación y fecha de registro'
                );
            }
        ),
    };

    if (
        isClosingStatus ||
        isFollowUpStatus ||
        isStatusExtensionInfo ||
        isStatusReceptionInfo ||
        isStatusSolution ||
        isStatusProcessed ||
        isStatusExtension
    ) {
        schema.customerFilingDate = schema.customerFilingDate.required(
            'La fecha de radicación cliente es obligatoria'
        );
        schema.sacDate = Yup.date().required('La fecha de SAC es obligatoria');
    }
    if (isStatusProcessed) {
        schema.notificationProcedureDate =
            schema.notificationProcedureDate.required(
                'La fecha de notificación trámite es obligatoria'
            );
    }
    if (isClosingStatus || isStatusSolution) {
        schema.responseDate = schema.responseDate.required(
            'La fecha de respuesta es obligatoria'
        );
        schema.notificationResponseDate =
            schema.notificationResponseDate.required(
                'La fecha de notificación respuesta es obligatoria'
            );
    }
    if (isClosingStatus) {
        schema.closingDate = Yup.date().required(
            'La fecha de cierre es obligatoria'
        );
    }

    return Yup.object(schema);
};
